/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import * as CD from 'app/cd/models/essette-api';
import { IPopup } from 'app/shared/elements/popup-base';
import { apiCall } from 'app/shared/lib/essette-api';
import { IMemberSearchFilters, MemberService } from 'app/cd/services/member-service';
import { LookupService } from 'app/core/services/lookup-service';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import { EditAddress } from 'app/core/components/addresses/edit-address';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-typed-observable-plugin';
import { deepClone } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';


@autoinject()
export class EditMemberAddress extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected memberAddresses: Array<CD.MemberAddress> = [];

    protected memberSearchFilters: IMemberSearchFilters = {
        alternateTypes: false
    };

    protected editedAddress: CD.MemberAddress | null = null;
    protected editAddressIndex: number | null = null;
    protected types: Core.AddressType[] = [];
    protected allTypes: Core.AddressType[] = [];
    protected validate = validate;
    protected isValid: boolean = false;
    public deactiveAddressIds: string[] = [];

    ////****************** Element References ***************************************////
    private $saveAddressModal!: IPopup;
    protected $saveAddressForm!: IForm;
    protected $saveAddress!: EditAddress;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.loadAllTypes().then(() => {
            this.updateAvailableTypes();
        });
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////

    protected closeSaveAddressModal(): void {
        this.$saveAddress.address = {};
        this.$saveAddressModal.close();
    }

    protected openAddressModal(addressIndex?: number): void {
        if(addressIndex != null) {
            this.editAddressIndex = addressIndex;
            this.$saveAddress.address = deepClone(this.memberAddresses[addressIndex]);
        } else {
            this.editAddressIndex = null;
        }
        this.updateAvailableTypes(this.$saveAddress.address.type);
        if(this.$saveAddressModal) {
            this.$saveAddressModal.open();
        }
    }

    protected addAddress(): void {
        if(!this.memberAddresses) this.memberAddresses = []; // sets addresses in the instance where it was explicitly set to null or undefined
        if(this.isValid) {
            const typeExists = this.memberAddresses.some(address => address.type?.id === this.$saveAddress.address.type?.id);
            if(typeExists) {
                this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.add_error', { what: this.i18n.tr('core:address.type.$'), item: this.i18n.tr('core:address.$') })).show();
            }
            else {
                const address = this.$saveAddress.address;
                this.memberAddresses.push(address);
                this.updateAvailableTypes();
                this.closeSaveAddressModal();
            }
        }
    }

    protected editAddress(addressIndex: number): void {
        const typeExists = this.memberAddresses.some(address => address.type?.id === this.$saveAddress.address.type?.id);
        //Check if form is valid and either the address is the same as the one being edited or a newly added address
        if(this.isValid && (this.memberAddresses[addressIndex].type?.id === this.$saveAddress.address.type?.id || !typeExists)) {
            this.memberAddresses.splice(addressIndex, 1);
            this.memberAddresses.push(this.$saveAddress.address);
            this.updateAvailableTypes();
            this.closeSaveAddressModal();
        } else {
            this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.edit_error', { what: this.i18n.tr('core:address.type.$'), item: this.i18n.tr('core:address.$') })).show();
        }
    }

    protected deleteAddress(addressIndex: number): void {
        if(addressIndex !== -1) {
            const removedAddressId = this.memberAddresses.splice(addressIndex, 1)[0].id;
            if(removedAddressId) {
                this.deactiveAddressIds.push(removedAddressId);
            }
            this.updateAvailableTypes();
        }
    }

    protected get isAddButtonDisabled(): boolean {
        return this.types.length === 0;
    }

    private updateAvailableTypes(includeType?: Core.AddressType | null): void {
        this.types = this.allTypes.filter(type =>
            !this.memberAddresses.some(address => address.type?.id === type.id) || (includeType?.id === type.id)
        );
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadAllTypes(): Promise<void> {
        try {
            this.state.beginLoad();

            const types = await this.memberService.loadMemberAddressTypes(undefined, undefined, this.memberSearchFilters);
            this.allTypes = types.results;
        } catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
